/**
 * Reconciliation — daily batches and exceptions from the live ledger.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, shortDate, tzs, errorMessage } from "../lib/admin";
import { adminApi } from "../lib/api";
import type { ReconBatch, ReconException } from "../lib/types";
import { AlertTriangle, CheckCircle2, RefreshCw, Calendar } from "lucide-react";

export function Recon() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [actionError, setActionError] = useState<string | null>(null);

  const batches = useAdminQuery<{ batches: ReconBatch[] }>(["recon-batches"], "/admin/v1/recon/batches");
  const exceptions = useAdminQuery<{ exceptions: ReconException[]; total: number }>(
    ["recon-exceptions"],
    "/admin/v1/recon/exceptions",
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin"] });
    setActionError(null);
  };

  const runBatch = useMutation({
    mutationFn: (day: string) => adminApi.post("/admin/v1/recon/batches", undefined, { query: { date: day } }),
    onSuccess: invalidate,
    onError: (error) => setActionError(errorMessage(error)),
  });

  const resolve = useMutation({
    mutationFn: (id: string) =>
      adminApi.post(`/admin/v1/recon/exceptions/${id}/resolve`, undefined, {
        query: { resolution: "Posted missing capture from admin recon" },
      }),
    onSuccess: invalidate,
    onError: (error) => setActionError(errorMessage(error)),
  });

  const batchRows = batches.data?.batches ?? [];
  const exceptionRows = exceptions.data?.exceptions ?? [];
  const balanced = batchRows.filter((b) => b.status === "balanced").length;

  return (
    <>
      <PageHead
        title="Reconciliation"
        description="Provider history vs internal attempts vs ledger."
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              className="ev-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Batch date"
            />
            <button
              className="ev-btn ev-btn-sm ev-btn-outline"
              disabled={runBatch.isPending}
              onClick={() => runBatch.mutate(date)}
            >
              <RefreshCw size={16} /> Run batch
            </button>
          </div>
        }
      />

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      <div className="admin-kpi-grid" style={{ marginBottom: 24 }}>
        <div className="admin-card">
          <span className="ev-body-muted">Batches (7 days)</span>
          <div className="ev-display-s">{batchRows.length}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Balanced</span>
          <div className="ev-display-s" style={{ color: "var(--ev-mint-600)" }}>{balanced}</div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Exceptions (7 days)</span>
          <div className="ev-display-s" style={{ color: "var(--ev-amber-600)" }}>
            {batchRows.reduce((sum, b) => sum + b.exceptions, 0)}
          </div>
        </div>
        <div className="admin-card">
          <span className="ev-body-muted">Open today</span>
          <div className="ev-display-s" style={{ color: "var(--ev-red-600)" }}>{exceptions.data?.total ?? 0}</div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
        <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
          <h3>Daily batches</h3>
        </div>
        {batches.isLoading && <div style={{ padding: 20 }}><LoadingCard label="Loading batches…" /></div>}
        {batches.error && <div style={{ padding: 20 }}><ErrorCard error={batches.error} /></div>}
        {batches.data && batchRows.length === 0 && <div style={{ padding: 20 }}><EmptyState>No batches yet.</EmptyState></div>}
        {batchRows.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Batch</th>
                  <th className="admin-table-th">Date</th>
                  <th className="admin-table-th">Orders</th>
                  <th className="admin-table-th">Gross</th>
                  <th className="admin-table-th">Exceptions</th>
                  <th className="admin-table-th">Status</th>
                </tr>
              </thead>
              <tbody>
                {batchRows.map((b) => (
                  <tr key={b.batch_id} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Batch"><span className="ev-body-mono">{b.batch_id}</span></td>
                    <td className="admin-table-td" data-label="Date">
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Calendar size={14} style={{ color: "var(--ev-slate-400)" }} />
                        <span className="ev-body">{shortDate(b.date)}</span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="Orders">{b.orders}</td>
                    <td className="admin-table-td" data-label="Gross"><span className="ev-body-mono ev-body-semibold">{tzs(b.gross_minor)}</span></td>
                    <td className="admin-table-td" data-label="Exceptions">{b.exceptions}</td>
                    <td className="admin-table-td" data-label="Status">
                      <span className={`ev-badge ${b.status === "balanced" ? "ev-badge-mint" : "ev-badge-amber"}`}>
                        {b.status === "balanced" ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                        {b.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="admin-card-header" style={{ padding: "16px 20px", marginBottom: 0 }}>
          <h3>Open exceptions (today)</h3>
        </div>
        {exceptions.isLoading && <div style={{ padding: 20 }}><LoadingCard label="Loading exceptions…" /></div>}
        {exceptions.error && <div style={{ padding: 20 }}><ErrorCard error={exceptions.error} /></div>}
        {exceptions.data && exceptionRows.length === 0 && (
          <div style={{ padding: 20 }}><span className="ev-body-muted">No open exceptions.</span></div>
        )}
        {exceptionRows.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Order</th>
                  <th className="admin-table-th">Type</th>
                  <th className="admin-table-th">Amount</th>
                  <th className="admin-table-th">Message</th>
                  <th className="admin-table-th" />
                </tr>
              </thead>
              <tbody>
                {exceptionRows.map((row) => (
                  <tr key={row.id} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Order"><span className="ev-body-mono">{row.order_ref}</span></td>
                    <td className="admin-table-td" data-label="Type"><span className="ev-badge ev-badge-amber">{row.type}</span></td>
                    <td className="admin-table-td" data-label="Amount"><span className="ev-body-mono ev-body-semibold">{tzs(row.amount_minor)}</span></td>
                    <td className="admin-table-td" data-label="Message"><span className="ev-body-micro">{row.message}</span></td>
                    <td className="admin-table-td" data-label="">
                      <button
                        className="ev-btn ev-btn-sm ev-btn-outline"
                        disabled={resolve.isPending}
                        onClick={() => resolve.mutate(row.id)}
                      >
                        Resolve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
